import { useDispatch, useSelector } from 'react-redux';
import { useRef, useState } from 'react';
import { updateOperatorConfiguration } from '../../../store/actions/operators';
import { BUSINESS_MODEL_DROPSHIP, BUSINESS_MODEL_MARKETPLACE, HANDLING_FEE_NONE } from '../../../constants/operators';
import {
  OperatorAutoSync,
  OperatorCarrier,
  OperatorCommissionSelector,
  OperatorHandlingFee,
  OperatorInvoicePaymentTerms,
  OperatorPaymentCycle,
  OperatorSla,
} from '../../../components/operators';
import ConfirmModal from '../../../components/modals/ConfirmDeleteModal/ConfirmModal';
import { OperatorRefundFee } from '../../../components/operators/OperatorRefundFee';

import styles from './OperatorSellerPage.module.scss';

export function OperatorConfigurations() {
  const dispatch = useDispatch();
  const {
    businessModel,
    configuration,
    loading: { submit: submitting },
  } = useSelector((state) => state.operators);
  const currentBusinessModel = useRef(businessModel);
  const [confirmModalOpened, setConfirmModalOpened] = useState(false);

  const dropshipSelected = businessModel === BUSINESS_MODEL_DROPSHIP;
  const marketplaceSelected = businessModel === BUSINESS_MODEL_MARKETPLACE;

  const updateConfigurations = () => {
    let data = {
      order_type: businessModel,
      auto_sync: configuration.autoSync,
      settings: {
        ecommerce_days_until_overdue_email: configuration.sla,
      },
    };

    if (dropshipSelected) {
      data = {
        ...data,
        replenishment_shipper_account: configuration.carrier?.account,
        replenishment_shipper_account_type: configuration.carrier?.carrier,
        ecommerce_send_invoice: !!configuration.invoice.enabled,
        ecommerce_invoice_net_plus: Number(configuration.invoice.value),
        operator_commission: null,
        refund_cost_formula: configuration.refundFee ? Math.round(configuration.refundFee * 100).toString() : 0,
        handling_fee_formula:
          configuration.handlingFee && configuration.handlingFee.type !== HANDLING_FEE_NONE
            ? {
                type: configuration.handlingFee.type,
                formula: Math.round(configuration.handlingFee.formula * 100).toString(),
              }
            : null,
      };
    } else if (marketplaceSelected) {
      data = {
        ...data,
        payment_cycle: configuration.paymentCycle,
        operator_commission: configuration.commission,
        handling_fee_formula: null,
        replenishment_shipper_account: null,
        replenishment_shipper_account_type: null,
        ecommerce_invoice_net_plus: 30,
        ecommerce_send_invoice: false,
      };
    }
    dispatch(updateOperatorConfiguration(data));
  };

  const submitOrConfirm = () => {
    if (currentBusinessModel.current !== businessModel) {
      setConfirmModalOpened(true);
    } else {
      updateConfigurations();
    }
  };

  return (
    <>
      <fieldset className={styles.sectionWrapper}>
        <h2>Configurations</h2>

        <div className={styles.field}>
          <OperatorCommissionSelector />
        </div>

        {dropshipSelected && (
          <div className={styles.field}>
            <OperatorHandlingFee />
          </div>
        )}

        {dropshipSelected && (
          <div className={styles.field}>
            <OperatorRefundFee />
          </div>
        )}

        {dropshipSelected && (
          <div className={styles.field}>
            <OperatorCarrier />
          </div>
        )}

        {dropshipSelected && (
          <div className={styles.field}>
            <OperatorInvoicePaymentTerms />
          </div>
        )}

        {marketplaceSelected && (
          <div className={styles.field}>
            <OperatorPaymentCycle />
          </div>
        )}

        <div className={styles.field}>
          <OperatorAutoSync />
        </div>

        <div className={styles.field}>
          <OperatorSla />
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.button} onClick={submitOrConfirm} disabled={submitting}>
            Save
          </button>
        </div>
      </fieldset>

      <ConfirmModal
        open={confirmModalOpened}
        text={[
          <span>
            Changing the business model will remove values from some fields (e.g., Commission, Handling Fees, Carrier,
            Invoice).
          </span>,
          <br />,
          <span>Please review and confirm before proceeding.</span>,
        ]}
        onConfirm={() => {
          updateConfigurations();
          currentBusinessModel.current = businessModel;
          setConfirmModalOpened(false);
        }}
        onCancel={() => {
          setConfirmModalOpened(false);
        }}
      />
    </>
  );
}
